/*
**  rui-button-group.js
**
**  rui-button-group component class
*/
import { LitElement, html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
export class RuiButtonGroup extends LitElement {
    static get is() {
        return 'rui-button-group';
    }
    static get properties() {
        return {
            value: { type: String },
            valueArray: { type: Array },
            isVertical: { type: Object },
            isMultiple: { type: Object }
        };
    }
    constructor() {
        super();
        this._value = null;
        this._valueArray = [];
        this._isVertical = false;
        this.isMultiple = false;
    }
    // Custom setter for value property
    set value(newValue) {
        if (this._value !== newValue) {
            // Store new value
            this._value = newValue;
            this.requestUpdate();
            // Fire value change event
            Promise.resolve().then(() => {
                let valueChangeEvent = new CustomEvent('rui-button-group-value-change', {
                    detail: newValue,
                    bubbles: true,
                    composed: true
                });
                this.dispatchEvent(valueChangeEvent);
            });
            // Set any option that has the current value as active, pausing to allow generated children to be added to the DOM
            setTimeout(() => {
                for (let i = 0; i < this.children.length; i++) {
                    if (this.children[i].tagName.toLowerCase() === 'rui-button-group-option') {
                        if (typeof newValue !== 'object') {
                            this.children[i]._isActive = this.children[i].value === String(newValue);
                        }
                        else {
                            this.children[i]._isActive = JSON.stringify(this.children[i].value) === JSON.stringify(newValue);
                        }
                    }
                }
            }, 10);
        }
    }
    // Custom getter for value property
    get value() {
        return this._value;
    }
    // Custom setter for valueArray property
    set valueArray(newValue) {
        if (this._valueArray !== newValue) {
            // Store new value
            this._valueArray = newValue;
            this.requestUpdate();
            // Fire value change event
            Promise.resolve().then(() => {
                let valueChangeEvent = new CustomEvent('rui-button-group-value-change', {
                    detail: newValue,
                    bubbles: true,
                    composed: true
                });
                this.dispatchEvent(valueChangeEvent);
            });
            // Set any option that has the current value as active, pausing to allow generated children to be added to the DOM
            setTimeout(() => {
                for (let i = 0; i < this.children.length; i++) {
                    if (this.children[i].tagName.toLowerCase() === 'rui-button-group-option') {
                        for (let j = 0; j < this._valueArray.length && !this.children[i]._isActive; j++) {
                            this.children[i]._isActive = this.valueArray[j] == this.children[i].value;
                        }
                    }
                }
            }, 10);
        }
    }
    // Custom getter for valueArray property
    get valueArray() {
        return this._valueArray;
    }
    // Custom setter for isVertical property
    set isVertical(newValue) {
        if (this._isVertical !== newValue) {
            // Store new value
            this._isVertical = newValue;
            this.requestUpdate();
            // Set any option that has the current value as active, pausing to allow generated children to be added to the DOM
            Promise.resolve().then(() => {
                for (let i = 0; i < this.children.length; i++) {
                    if (this.children[i].tagName.toLowerCase() === 'rui-button-group-option') {
                        this.children[i]._isVertical = newValue;
                    }
                }
            });
        }
    }
    // Custom getter for isVertical property
    get isVertical() {
        return this._isVertical;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('rui-button-group-option-click', (e) => this.optionClick(e));
    }
    disconnectedCallback() {
        this.removeEventListener('rui-button-group-option-click', (e) => this.optionClick(e));
        super.disconnectedCallback();
    }
    // Update the button group's value when one of its options is clicked
    optionClick(e) {
        // Stop event here
        e.preventDefault();
        e.stopPropagation();
        // Set button group's value
        if (!this.isMultiple) {
            // Single value: just store the new value
            this.value = e.detail;
        }
        else {
            // Multiple value: toggle the clicked option's presence in the value array
            // Is the clicked option already in the value array?
            let found = false;
            let foundIndex = 0;
            while (foundIndex < this._valueArray.length && !found) {
                if (this.valueArray[foundIndex] == e.detail) {
                    found = true;
                }
                else {
                    foundIndex++;
                }
            }
            // Build up new value array, setting any option that's in the updated value array as active too
            const newValueArray = [];
            for (let i = 0; i < this.children.length; i++) {
                if (this.children[i].tagName.toLowerCase() === 'rui-button-group-option') {
                    if (this.children[i].value == e.detail) {
                        this.children[i]._isActive = !found;
                    }
                    if (this.children[i]._isActive) {
                        newValueArray.push(this.children[i].value);
                    }
                }
            }
            // Update value array with current values
            this._valueArray = newValueArray;
            // Fire value change event
            Promise.resolve().then(() => {
                let valueChangeEvent = new CustomEvent('rui-button-group-value-change', {
                    detail: this._valueArray,
                    bubbles: true,
                    composed: true
                });
                this.dispatchEvent(valueChangeEvent);
            });
        }
    }
    render() {
        const classes = {
            ['rui-button-group']: true,
            ['rui-button-group-vertical']: this._isVertical
        };
        return html `
            <div class="${classMap(classes)}">
                <slot></slot>
            </div>
        `;
    }
    static get styles() {
        return css `
            .rui-button-group {
                align-items: center;
                box-sizing: border-box;
                display: flex;
                font-family: var(--font-family, "Segoe UI", "Open Sans", "Helvetica Neue", Arial, sans-serif);
                font-size: var(--btn-font-size-md, 14px);
                font-weight: var(--btn-font-weight, 400);
                justify-content: flex-start;
                line-height: var(--btn-line-height-md, 1.42857143);
                position: relative;
                line-height: 1.42857143;
                vertical-align: middle;
                -webkit-font-smoothing: antialiased;
            }
            
            .rui-button-group-vertical {
                display: inline-block;
            }
        `;
    }
}
customElements.get(RuiButtonGroup.is) || customElements.define(RuiButtonGroup.is, RuiButtonGroup);
